import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn
} from "@angular/forms";
import { IndividualData } from "@models/individuals/individual-data";
import { CorporateHolderModel, SecurityRegistryRecord } from "@models/shares/security-registry-record";
import {
  AuthSignatoryComponent,
  IAuthSignatoryFormControl
} from "@shared/components/shares/auth-signatory/auth-signatory.component";
import { CustomFormValidators } from "@shared/validators/custom-form-validators";
import {
  ReusableFormGroupComponent
} from "../../reusable-form-groups/reusable-form-group/reusable-form-group.component";

export interface IAuthorisedSignatoriesFormGroupControls {
  corporateHoldersAuthorisedSignatories: FormArray<FormControl<IndividualData[]>>;
}

export type IAuthorisedSignatoriesFormGroup = FormGroup<IAuthorisedSignatoriesFormGroupControls>;

@Component({
  selector: 'app-authorised-signatories-step',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AuthSignatoryComponent
  ],
  templateUrl: './authorised-signatories-step.component.html',
  styleUrl: './authorised-signatories-step.component.scss'
})
export class AuthorisedSignatoriesStepComponent extends ReusableFormGroupComponent<FormGroup> {
  @Input() title = '';
  @Input() authSignatoriesValidators: ValidatorFn[] = [];

  @Input({
    alias: 'securityRegistryRecords',
    required: true
  }) set securityRegistryRecordSetter(value: SecurityRegistryRecord[] | null) {
    if (!value) {
      return;
    }

    this.securityRegistryRecords = value.filter((record) =>
      record.holders.some((holder) => holder.$type === CorporateHolderModel.$type)
    );

    this.corporateHoldersAll = this.securityRegistryRecords
      .flatMap((record) => record.holders)
      .filter((holder) => holder.$type === CorporateHolderModel.$type) as CorporateHolderModel[];

    this.corporateHolders = Array.from(
      this.corporateHoldersAll
        .reduce((map, holder) => {
          if (!map.has(holder.name) || holder.isPosted) {
            map.set(holder.name, holder as CorporateHolderModel);
          }
          return map;
        }, new Map<string, CorporateHolderModel>())
        .values()
    ) as CorporateHolderModel[];

    this.setupAuthorisedSignatoriesForm(this.corporateHolders);
  }

  @Output() corporateHolderChange = new EventEmitter<CorporateHolderModel>();

  readonly authSignatoriesCustomErrors = {
    startsWithDigit: 'The name must start with a letter.',
    oneLetterPartName: 'Initials aren\'t allowed.',
    atLeastOneSign: 'The authorised signatories must contain at least one sign.',
    restrictEmptySigns: 'The authorised signatories must not contain empty signs.',
    maxSignatoriesLimit: `A maximum of two authorised signatories can be added.`,
  };

  securityRegistryRecords: SecurityRegistryRecord[] = [];
  corporateHoldersAll: CorporateHolderModel[] = [];
  corporateHolders: CorporateHolderModel[] = [];
  override form: IAuthorisedSignatoriesFormGroup = AuthorisedSignatoriesStepComponent.defineForm();

  onDataSaved(authSignatories: IndividualData[], holder: CorporateHolderModel): void {
    holder.details.authorisedSignatories = authSignatories;
    this.corporateHolderChange.emit(holder);
    const signRecord: Record<string, IndividualData[]> = {};
    this.corporateHoldersAll.forEach((holder) => {
      signRecord[holder.relationshipId] = holder.details.authorisedSignatories ?? [];
    });
    this.updateValidation();
  }

  updateValidation(): void {
    this.form.updateValueAndValidity();
    this.form.controls.corporateHoldersAuthorisedSignatories.updateValueAndValidity();
  }

  private setupAuthorisedSignatoriesForm(corporateHolders: CorporateHolderModel[]): void {
    const signRecord: Record<string, IndividualData[]> = {};
    const signsControls = corporateHolders.map((holder) => {
      const authorisedSignatories = holder.details.authorisedSignatories ?? [];
      signRecord[holder.relationshipId] = authorisedSignatories;
      return this.createAuthorisedSignatoryForm(authorisedSignatories);
    });
    this.form.setControl('corporateHoldersAuthorisedSignatories', new FormArray<FormControl<IndividualData[]>>(signsControls));
  }

  private createAuthorisedSignatoryForm(value: IndividualData[]): FormControl<IndividualData[]> {
    return this.setupAuthorisedSignatoryFormValidator(new FormControl<IndividualData[]>(value, { nonNullable: true }));
  }

  private setupAuthorisedSignatoryFormValidator(control: FormControl<IndividualData[]>): FormControl<IndividualData[]> {
    const atLeastOneSignValidator = (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as IndividualData[] ?? [];
      return value.length > 0 ? null : { atLeastOneSign: true };
    };

    const restrictEmptySigns = (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as IndividualData[] ?? [];
      const noEmptySigns = value.every((sign) => sign.firstName && sign.lastName);
      return noEmptySigns ? null : { restrictEmptySigns: true };
    };

    control.setValidators([
      CustomFormValidators.directorNameValidator,
      CustomFormValidators.directorOneLetterNameValidator,
      atLeastOneSignValidator,
      restrictEmptySigns,
      ...this.authSignatoriesValidators
    ]);

    return control;
  }

  get control(): FormGroup | null {
    return this.form.get('corporateHoldersAuthorisedSignatories') as FormGroup ?? null;
  }

  static override defineForm(): IAuthorisedSignatoriesFormGroup {
    return new FormGroup(AuthorisedSignatoriesStepComponent.defineFormControls());
  }

  static override defineFormControls(): IAuthorisedSignatoriesFormGroupControls {
    return { corporateHoldersAuthorisedSignatories: new FormArray<IAuthSignatoryFormControl>([]) };
  }

  static maxSignatoriesLimitValidator(max = 2): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value as IndividualData[] ?? [];

      return value?.length > max
        ? { maxSignatoriesLimit: `A maximum of ${ max } authorised signatories can be added.` }
        : null;
    };
  }
}
